// 2D camera: world↔screen transform, pan, zoom, edge-scroll, and map-bounds clamping.
// Rendering is done in world coordinates after applyTransform(); the HUD draws in screen
// coordinates with the identity transform.

import { clamp, type Vec2 } from "../engine/vec";

/**
 * The camera surface that input.ts and game.ts depend on. Both the 2D orthographic
 * `Camera` and the PlayCanvas `PcCamera` implement it, so the input layer and scene
 * wiring are renderer-agnostic. `x`/`y` are the world-space focus point (sim coords),
 * `zoom` a logical scalar; `worldToScreen`/`screenToWorld` map sim world coords (2D,
 * the ground plane) to/from CSS-pixel screen coords using whatever projection the
 * concrete camera implements.
 */
export interface ICamera {
  x: number;
  y: number;
  zoom: number;
  vw: number;
  vh: number;
  dpr: number;
  setViewport(w: number, h: number, dpr: number): void;
  centerOn(p: Vec2): void;
  worldToScreen(p: Vec2): Vec2;
  screenToWorld(p: Vec2): Vec2;
  panScreen(dx: number, dy: number): void;
  zoomAt(screenPt: Vec2, factor: number): void;
  clampToMap(mapW: number, mapH: number): void;
}

export class Camera implements ICamera {
  x = 0; // world coords at viewport center
  y = 0;
  zoom = 0.8;
  vw = 1280; // css pixels
  vh = 720;
  dpr = 1;
  minZoom = 0.42;
  maxZoom = 1.8;

  setViewport(w: number, h: number, dpr: number): void {
    this.vw = w;
    this.vh = h;
    this.dpr = dpr;
  }

  centerOn(p: Vec2): void {
    this.x = p.x;
    this.y = p.y;
  }

  worldToScreen(p: Vec2): Vec2 {
    return { x: (p.x - this.x) * this.zoom + this.vw / 2, y: (p.y - this.y) * this.zoom + this.vh / 2 };
  }

  screenToWorld(p: Vec2): Vec2 {
    return { x: (p.x - this.vw / 2) / this.zoom + this.x, y: (p.y - this.vh / 2) / this.zoom + this.y };
  }

  applyTransform(ctx: CanvasRenderingContext2D): void {
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.translate(this.vw / 2, this.vh / 2);
    ctx.scale(this.zoom, this.zoom);
    ctx.translate(-this.x, -this.y);
  }

  resetTransform(ctx: CanvasRenderingContext2D): void {
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  panScreen(dx: number, dy: number): void {
    this.x += dx / this.zoom;
    this.y += dy / this.zoom;
  }

  zoomAt(screenPt: Vec2, factor: number): void {
    const before = this.screenToWorld(screenPt);
    this.zoom = clamp(this.zoom * factor, this.minZoom, this.maxZoom);
    const after = this.screenToWorld(screenPt);
    this.x += before.x - after.x;
    this.y += before.y - after.y;
  }

  clampToMap(mapW: number, mapH: number): void {
    // allow a little overscroll margin
    const m = 200;
    this.x = clamp(this.x, -m, mapW + m);
    this.y = clamp(this.y, -m, mapH + m);
  }
}
